import cx from 'clsx';
import * as React from 'react';

import AssetLayer from '@/components/AssetLayer/AssetLayer';
import { LayersWrapper } from '@/components/game-tile';
import JackpotLayerGameTile from '@/components/game-tile/JackpotLayer/JackpotLayerGameTile';
import LiveTableLayer from '@/components/game-tile/LiveTableLayer/LiveTableLayer';
import SashLayer from '@/components/game-tile/SashLayer/SashLayer';
import { SashLayerProps } from '@/components/game-tile/SashLayer/types';
import GameLink from '@/components/GameLink';
import { Info } from '@/components/icons';
import {
  MEDIUM_BREAKPOINT,
  SMALL_BREAKPOINT,
} from '@/components/modular-grid/constants';
import { BannerType } from '@/components/modular-grid/types';
import { prepareGameInfoTooltip } from '@/components/modular-grid/utils';
import { useWindowSize } from '@/hooks/useWindowSize';
import { CategoryFinancialWebAttributes } from '@/types/category/categoryTypes';
import { ImageType, JackpotType, Table } from '@/types/gameTypes';

import styles from './Banner.module.scss';
import createSizedSources from './createSizedSources';
import logo from './logo.png';

interface BannerComponentProps {
  ghost?: boolean;

  /**
   * Small asset for mobile and tablet.
   */
  asset?: {
    type: BannerType;
  } & ImageType;

  /**
   * Large asset for desktop
   *
   * Naming is very bad, but it's hard to fix because in Contentstack it's
   * `small_large_asset`. We cannot change the UID of the field easily because
   * all data will be lost.
   */
  smallLargeAsset?: {
    type: BannerType;
  } & ImageType;

  /**
   * TODO GT-8627 clean up
   * @deprecated The medium image is not used in New Vegas
   */
  mediumAsset?: {
    type: BannerType;
  } & ImageType;
  gameBannerTitle?: string;
  gameLink?: {
    gameDetailsPath?: string;
    url: string;
    target: string;
    onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
    shouldMapUrl?: boolean;
    integration?: string;
    notIndexed?: boolean;
  };
  info?: {
    visible?: boolean;
    onClick?: (event: React.MouseEvent) => void;
    gameInfoTooltip?: string;
  };
  sashLayer?: SashLayerProps;
  tableLayer?: Table;
  jackpotLayer?: JackpotType;
  personalization?: {
    selector: string;
    slug?: string;
    rendered?: boolean;
  };
  cloned?: boolean;
  index?: number;
  categoryAttributes?: CategoryFinancialWebAttributes;
  infoIcon?: boolean;
  rounded?: 'all' | 'top' | 'none';
}

const BannerComponent: React.FC<BannerComponentProps> = ({
  asset,
  smallLargeAsset,
  mediumAsset,
  gameLink,
  ghost,
  sashLayer,
  gameBannerTitle,
  tableLayer,
  personalization,
  cloned,
  jackpotLayer,
  categoryAttributes,
  index = 0,
  info,
  infoIcon = true,
  rounded = 'all',
}) => {
  const windowWidth = useWindowSize().width;
  const preparedGameInfoTooltip = prepareGameInfoTooltip(
    info?.gameInfoTooltip,
    gameBannerTitle || ''
  );

  const isDesktop = windowWidth > MEDIUM_BREAKPOINT.max;

  return !personalization ? (
    <div className={styles.bannerComponentWrapper}>
      <div className={styles.slotWrapper} data-testid="banner-slot">
        <div
          className={cx(
            smallLargeAsset ? styles.fileBanner : styles.file,
            ghost && styles.ghost
          )}
        >
          <LayersWrapper rounded={isDesktop ? undefined : 'all'}>
            <AssetLayer
              type={
                smallLargeAsset ? smallLargeAsset.type : asset?.type || 'image'
              }
              sizedSources={createSizedSources(
                smallLargeAsset,
                asset
              )}
              imageSrc={asset?.source}
              alt={asset?.alt}
              isLazy={true}
              loadingAnimation={false}
              videoAttributes={{
                autoPlay: true,
                loop: true,
                playsInline: true,
                muted: true,
              }}
              videoSrc={asset && asset.source.normal}
              // TODO - tmp solution for banner on mobile
              imageAttributes={
                windowWidth <= SMALL_BREAKPOINT.max
                  ? {
                      style: {
                        maxWidth: '100%',
                        objectPosition: 'left bottom',
                        objectFit: 'cover',
                      },
                    }
                  : {
                      style: {
                        objectPosition: 'left bottom',
                        objectFit: 'cover',
                      },
                    }
              }
              rounded={rounded}
            />
          </LayersWrapper>
          {gameLink && (
            <GameLink
              url={gameLink.url}
              target={gameLink.target}
              shouldMapUrl={gameLink.shouldMapUrl}
              integration={gameLink.integration}
              onClick={gameLink.onClick}
              notIndexed={gameLink.notIndexed}
              categoryAttributes={categoryAttributes}
            />
          )}
          {tableLayer && (
            <LiveTableLayer table={tableLayer} outfit={'banner'} />
          )}
          {infoIcon && info?.visible && (
            <a
              className={cx(styles.link, styles.infoGameBannerWrapper)}
              target="_blank"
              rel="noopener noreferrer"
              href={gameLink?.gameDetailsPath}
              title={preparedGameInfoTooltip}
            >
              <div
                className={styles.info}
                data-testid="game-info"
                onClick={e => {
                  e.preventDefault();
                  info.onClick && info.onClick(e);
                }}
              >
                <Info altText={preparedGameInfoTooltip} />
              </div>
            </a>
          )}
          {jackpotLayer && !tableLayer && (
            <JackpotLayerGameTile {...jackpotLayer} />
          )}
          {sashLayer && <SashLayer {...sashLayer} />}
        </div>
      </div>
    </div>
  ) : (
    <div className={styles.bannerComponentWrapper}>
      <div
        data-testid="banner-slot"
        className={cx(
          cloned
            ? personalization.selector.replace('.', '') + '_clone'
            : personalization.selector.replace('.', ''),
          personalization.rendered !== true && styles.hidden
        )}
      />
      {personalization.rendered === undefined ? (
        <div className={cx(styles.spinnerSlotWrapper)}>
          <div className={cx(styles.ghost)}>
            <div className={styles.spinnerWrapper}>
              <img className={styles.logo} src={logo} alt="Vegas logo" />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default BannerComponent;
